import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Instagram, Receipt } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSettings } from "@/context/SettingsContext";
import { telLink } from "@/lib/api";

export default function Footer() {
  const s = useSettings();
  const links = [
    ["Products", "/products"],
    ["Brands", "/brands"],
    ["About", "/about"],
    ["Gallery", "/gallery"],
    ["Reviews", "/reviews"],
    ["FAQ", "/faq"],
    ["Contact", "/contact"],
    ["Request a Quote", "/quote"],
  ];
  return (
    <footer className="bg-[#111111] pb-24 text-[#c9cdd4] md:pb-0" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo dark />
          <p className="mt-5 text-sm leading-relaxed">{s.tagline}</p>
          <p className="mt-2 text-xs text-[#667085]">Est. {s.established} · {s.location}</p>
        </div>
        <div>
          <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B700]">Contact</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li className="flex gap-2.5">
              <MapPin size={16} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
              <a href={s.maps_url} target="_blank" rel="noopener noreferrer" data-testid="footer-address" className="hover:text-white">
                {s.address_line1}, {s.address_line2}
              </a>
            </li>
            <li className="flex gap-2.5">
              <Phone size={16} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
              <a href={telLink(s)} data-testid="footer-phone" className="hover:text-white">{s.phone}</a>
            </li>
            <li className="flex gap-2.5">
              <Mail size={16} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
              <a href={`mailto:${s.email}`} data-testid="footer-email" className="hover:text-white">{s.email}</a>
            </li>
            <li className="flex gap-2.5">
              <Clock size={16} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
              <span>{s.hours_weekday}<br />{s.hours_sunday}</span>
            </li>
            {s.gstin ? (
              <li className="flex gap-2.5">
                <Receipt size={16} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                <span data-testid="footer-gstin">GSTIN: {s.gstin}</span>
              </li>
            ) : null}
            {s.social_instagram ? (
              <li className="flex gap-2.5">
                <Instagram size={16} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
                <a href={s.social_instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="hover:text-white">
                  Instagram — @hariomtradingco2
                </a>
              </li>
            ) : null}
          </ul>
        </div>
        <div>
          <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B700]">Quick Links</h3>
          <ul className="mt-5 grid grid-cols-2 gap-2.5 text-sm md:grid-cols-1">
            {links.map(([label, to]) => (
              <li key={to}>
                <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="hover:text-white">
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B700]">Product Range</h3>
          <ul className="mt-5 space-y-2.5 text-sm">
            {["Industrial Automation", "Switchgear", "Lighting", "Pneumatic Products", "Electrical Products", "Industrial Tools", "Fans"].map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-[#2c2c2e]">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-[#667085] sm:px-6 md:flex-row">
          <p data-testid="footer-copy">© {new Date().getFullYear()} {s.business_name} All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-white">Terms</Link>
            <Link to="/admin" data-testid="footer-admin" className="hover:text-white">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
